import { ACCEPTED_MEDIA_TYPES, MAX_MEDIA_SIZE } from "constants/form"
import { receiveErrors } from "modules/errors/reducer"
import { serialize } from "object-to-formdata"
import React, { useCallback, useEffect, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { CustomInputError } from "ui/CustomInputError"
import { MediaGrid } from "ui/MediaGrid/SortableMediaGrid"
import { MediaUpload } from "ui/MediaUpload"
import { DragAndDrop } from "ui/PersonalInfo/ProfileImages/DragAndDrop"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import { Button, Col, Flex, Form, message, Row } from "antd"
import { spacings } from "themes/variables"
import { Play } from "react-feather"
import ProfilePreview from "ui/PersonalInfo/ProfilePreview"
import { useDevice } from "hooks/useDevice"
import { guideSelector, updateGuide } from "store/user"
import useFormErrors from "hooks/useAntdFormErrors"
import useActiveGuide from "hooks/useActiveClient"

const MAX_COUNT = 10

export const ProfileImages = React.forwardRef(({ showActions = true, onStateChange }, ref) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isMobile } = useDevice()
  const { id, display_name, company_name, website, description, ...guide } = useSelector(guideSelector)
  const [localMedia, setLocalMedia] = useState([...guide.photos, ...(guide.videos || [])]?.sort((a, b) => a?.position - b?.position))
  const { errors } = useFormErrors(form)
  const filteredMedia = localMedia?.filter((photo) => !photo?._destroy)
  const activeMediaLength = filteredMedia?.length
  const hasMedia = activeMediaLength > 0
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const activePhotosLength = localMedia?.filter((photo) => !photo?._destroy)?.length
  const [showPreview, setShowPreview] = useState(false)
  const [localMediaAttributes, setLocalMediaAttributes] = useState([])
  const { isActive: active } = useActiveGuide()
  const hasChanges = localMediaAttributes.length > 0
  const canSave = active && activeMediaLength >= 2

  const handleDelete = (media) => {
    if (!media?.uid && media?.url) {
      const updatedPhotos = localMedia?.map((item) => (item?.id === media?.id ? { ...item, _destroy: true } : item))
      const deletedPhotoIndex = updatedPhotos?.findIndex((item) => item?.id === media?.id)
      const updatedPhotosWithPositions = updatedPhotos?.map((item, index) => {
        if (index > deletedPhotoIndex && !item?._destroy) {
          return { ...item, position: item?.position - 1 }
        }
        return item
      })
      setLocalMedia(updatedPhotosWithPositions)
      setLocalMediaAttributes(
        updatedPhotosWithPositions?.map(({ id, file_type, preview_url, blob, _destroy, position, file }) => ({
          id,
          file,
          file_type,
          preview_url,
          blob,
          _destroy,
          position
        }))
      )
    } else {
      const mediaPosition = localMedia?.find((item) => item?.id === media?.id)?.position
      const filteredPhotos = localMedia?.filter((item) => item?.id !== media?.id)
      const updatedPositions = filteredPhotos?.map((item) => {
        if (item?.position > mediaPosition) {
          return { ...item, position: item?.position - 1 }
        }
        return item
      })
      setLocalMedia(updatedPositions)
      setLocalMediaAttributes(
        updatedPositions.map(({ id, file_type, preview_url, blob, file, _destroy, position }) => ({
          id,
          file_type,
          preview_url,
          blob,
          _destroy,
          file,
          position
        }))
      )
    }
  }

  const handleReorder = (newItems) => {
    setLocalMedia(
      newItems.map(({ id }, index) => ({
        ...localMedia?.find((item) => item?.id === id),
        position: index
      }))
    )
    setLocalMediaAttributes(
      newItems.map(({ id, file_type, preview_url, blob, _destroy, file }, index) => ({
        id,
        file_type,
        preview_url,
        blob,
        file,
        _destroy,
        position: index
      }))
    )
  }

  const onFinish = useCallback((_formData, callback, { showToast = true } = {}) => {
    const safeCallback = typeof callback === "function" ? callback : () => {}
    const attributes = localMediaAttributes?.map(({ id, file_type, blob, _destroy, position, file }) => ({
      file_type,
      _destroy,
      position,
      file,
      ...(file ? {} : { id }),
      ...(file_type === "video" ? { preview: blob } : {})
    }))

    if (attributes?.length > 0) {
      const serializedFormData = serialize({ guide: { media_attributes: attributes } }, { indices: true })
      return dispatch(updateGuide(id, serializedFormData)).then(() => {
        setLocalMediaAttributes([])
        if (showToast) {
          message.success(t("profile_image.images_upload_success_message"))
        }
        safeCallback()
      })
    } else {
      safeCallback()
      return Promise.resolve()
    }
  }, [dispatch, id, localMediaAttributes, t])

  const onFileListChange = (photos, photosAttributes) => {
    setLocalMedia((prev) => [...prev, ...photos])
    setLocalMediaAttributes((prev) => [...prev, ...photosAttributes])
    dispatch(
      receiveErrors({
        data: { message: { profile_images: [] } },
        showToast: false
      })
    )
  }

  const handlePreviewToggle = () => setShowPreview((prev) => !prev)

  const handleMediaClick = (media) => {
    if (isMediaFileVideo(media)) {
      setSelectedMedia(media)
      setVideoModalOpen(true)
    }
  }

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false)
    setSelectedMedia(null)
  }

  const handleCancel = useCallback(() => {
    const savedMedia = [...(guide?.photos || []), ...(guide?.videos || [])]?.sort((a, b) => a?.position - b?.position)

    setLocalMedia(savedMedia)
    setLocalMediaAttributes([])

    dispatch(
      receiveErrors({
        data: { message: { profile_images: [] } },
        showToast: false
      })
    )
  }, [dispatch, guide?.photos, guide?.videos])

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ hasChanges, canSave })
    }
  }, [hasChanges, canSave, onStateChange])

  useImperativeHandle(ref, () => ({
    save: ({ showToast = true } = {}) => {
      if (hasChanges && !canSave) return Promise.resolve()
      return onFinish(undefined, () => {}, { showToast })
    },
    cancel: handleCancel,
    hasChanges,
    canSave
  }), [hasChanges, canSave, handleCancel, onFinish])

  return (
    <>
      <Row>
        <Col lg={24} md={24} xs={24} span={24}>
          {errors?.listing_media && (
            <div className="pb-s">
              <CustomInputError errorKey={errors?.listing_media[0]} />
            </div>
          )}
          <Flex justify="space-between" align="center">
            <span className="py-4">{t("profile_image.help_text")}</span>
            {!isMobile && (
              <Button
                color="primary"
                size="large"
                variant="outlined"
                disabled={!active}
                onClick={active ? handlePreviewToggle : undefined}
                icon={<Play size={16} />}
              >
                {t("global.preview")}
              </Button>
            )}
          </Flex>

          {hasMedia ? (
            <>
              <MediaUpload
                className="mb-m"
                imageInfo={t("media_upload.image_size_limit", { maxSize: MAX_MEDIA_SIZE.image })}
                videoInfo={t("media_upload.video_size_limit", { maxSize: MAX_MEDIA_SIZE.video })}
                onFileListChange={onFileListChange}
                lastPosition={activePhotosLength}
                maxCount={MAX_COUNT}
                maxCountErrorMessage={`${t("listings.tabs.media.max_photos_error")} ${MAX_COUNT}`}
                initialCount={activeMediaLength}
                cropAspect={1}
                acceptedTypes={[...ACCEPTED_MEDIA_TYPES.image, ...ACCEPTED_MEDIA_TYPES.video]}
              />
              <MediaGrid
                key={filteredMedia.length}
                mediaItems={filteredMedia}
                withActions
                onDelete={handleDelete}
                onReorder={handleReorder}
                lastPosition={activePhotosLength}
                onImageClick={handleMediaClick}
                showHeroImageLabel
              />
            </>
          ) : (
            <DragAndDrop
              onFileListChange={onFileListChange}
              maxCount={MAX_COUNT}
              maxCountErrorMessage={`${t("listings.tabs.media.max_photos_error")} ${MAX_COUNT}`}
              initialCount={activeMediaLength}
            />
          )}
          {showActions && (
            <Flex className="save-wrapper" justify="flex-start" gap={spacings.S} style={{ marginTop: "86px", marginBottom: "16px" }}>
              <Button
                className="pr-xl pl-xl"
                color="primary"
                variant="outlined"
                size="large"
                disabled={!active}
                onClick={active ? handleCancel : undefined}
              >
                {t("global.cancel")}
              </Button>
              <Button
                className="pr-xl pl-xl"
                color="primary"
                variant="solid"
                size="large"
                disabled={!active || activeMediaLength < 2}
                onClick={active ? onFinish : undefined}
              >
                {t("global.save")}
              </Button>
            </Flex>
          )}
        </Col>
      </Row>

      <VideoPlayerModal open={videoModalOpen} handleClose={handleCloseVideoModal} selectedMedia={selectedMedia} />
      <ProfilePreview
        show={showPreview}
        tab="about"
        guide={{
          ...guide,
          videos: localMedia?.filter((media) => isMediaFileVideo(media)),
          photos: localMedia?.filter((media) => !isMediaFileVideo(media)),
          display_name: form?.getFieldValue("display_name"),
          description: form?.getFieldValue("description")
        }}
        onClose={handlePreviewToggle}
      />
    </>
  )
})

ProfileImages.displayName = "ProfileImages"
